import React from 'react'
import { CSSTransition } from 'react-transition-group'
import styled from '@emotion/styled'
import {
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalMainContainer,
  ModalContent,
} from 'components/patterns/Modal'
import { Header } from 'components/patterns'
import strings from 'utils/strings'
import {
  ModalBodyLayout,
  ModalContentContainerLayout,
  ModalHeaderLayout,
} from 'components/patterns/Modal/Modal.style'
import PropTypes from 'prop-types'
import Help from 'components/Help'
import { themeColor } from 'utils/styles'

const HelpModalContext = React.createContext()

export const useHelpModal = () => React.useContext(HelpModalContext)

export const HelpModalProvider = ({ children, timeout = 300 }) => {
  const [content, updateModal] = React.useState(null)
  const [visible, updateVisibility] = React.useState(false)
  const [showWidget, updateShowHelpWidget] = React.useState(true)

  const openModal = (content) => {
    updateVisibility(true)
    updateModal(content)
  }

  const closeModal = () => {
    updateVisibility(false)
    setTimeout(() => {
      updateModal(null)
    }, timeout)
  }

  const showHelpWidget = () => updateShowHelpWidget(true)

  const hideHelpWidget = () => updateShowHelpWidget(false)

  return (
    <HelpModalContext.Provider
      value={{
        openModal,
        closeModal,
        visible,
        timeout,
        showHelpWidget,
        hideHelpWidget,
      }}
    >
      <HelpModal visible={visible} onClose={closeModal} timeout={timeout}>
        {content}
      </HelpModal>
      {children}
      {showWidget && <Help />}
    </HelpModalContext.Provider>
  )
}

HelpModalProvider.propTypes = {
  children: PropTypes.node,
  timeout: PropTypes.number,
}

export default function HelpModal({ visible, onClose, children, timeout }) {
  const $modalBody = React.createRef()
  const handleClose = () => {
    onClose()
  }

  const resetScroll = () => {
    if ($modalBody?.current) {
      $modalBody.current.scrollTo({ top: 0 })
    }
  }

  React.useEffect(() => {
    resetScroll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children])

  return (
    <HelpModalContainer>
      <CSSTransition
        classNames="HelpModalLayout"
        timeout={timeout}
        in={visible}
      >
        <HelpModalLayout className="HelpModalLayout">
          <ModalHeader>
            <StyledHelpModalHeader type="h3">
              {strings.chec_help}
            </StyledHelpModalHeader>
            <ModalCloseButton onClick={handleClose} />
          </ModalHeader>
          <ModalBody ref={$modalBody}>
            <ModalMainContainer>
              <ModalContent>{children}</ModalContent>
            </ModalMainContainer>
          </ModalBody>
        </HelpModalLayout>
      </CSSTransition>
    </HelpModalContainer>
  )
}

HelpModal.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func,
  visible: PropTypes.bool,
}

const HelpModalContainer = styled.div``

const HelpModalLayout = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 1000;
  background-color: white;
  transition: transform 300ms cubic-bezier(0.9, 0, 0.3, 0.7);
  transform: translateX(-100%);
  width: 350px;
  min-height: 200px;
  max-height: calc(100vh - 130px);
  position: fixed;
  border-radius: 15px;
  bottom: 67px;
  box-shadow: 0 0 5px rgba(108, 24, 186, 0.4);

  h1 {
    font-size: 18px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.22;
    letter-spacing: normal;
    text-align: left;
    color: #6c18ba;
  }

  &.HelpModalLayout-exit-done {
    transform: translateX(-100%);
  }

  &.HelpModalLayout-enter-done {
    transform: translateX(calc(0% + 10px));
  }

  ${ModalHeaderLayout} {
    border-radius: 15px 15px 0 0;
  }

  ${ModalContentContainerLayout} {
    max-height: 100%;
    background-color: ${themeColor('whitePrimary')};
  }

  ${ModalBodyLayout} {
    min-height: 0;
    overflow-y: auto;
    margin-bottom: 16px;
    &::-webkit-scrollbar {
      width: 5px;
    }

    &::-webkit-scrollbar-track:vertical {
      -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0);
      border-radius: 5px;
    }

    &::-webkit-scrollbar-thumb:vertical {
      border-radius: 5px;
      -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
    }
  }

  article {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 16px;

    && h2,
    p {
      color: ${themeColor('blackSecondary')};
    }
  }

  article h1 {
    font-size: 16px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.38;
    letter-spacing: normal;
    text-align: left;
    color: rgba(7, 7, 15, 0.87);
  }

  article h2 {
    font-family: Arial;
    font-size: 16px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.38;
    letter-spacing: normal;
    text-align: left;
    color: rgba(7, 7, 15, 0.87);

    em {
      font-style: normal;
      font-weight: lighter;
    }
  }

  article p {
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.29;
    letter-spacing: normal;
    text-align: left;
    color: rgba(7, 7, 15, 0.87);
  }

  article p:last-of-type {
    margin-bottom: 0;
  }

  article section {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 16px;
    padding: 12px;
    border-radius: 8px;
    background-color: #f1f2f3;
  }

  article section > svg:first-of-type {
    display: block;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
  }

  article figure {
    margin-top: 5px;
  }

  article figcaption {
    font-size: 10px;
    font-weight: normal;
    font-stretch: normal;
    font-style: italic;
    line-height: 1.8;
    letter-spacing: normal;
    text-align: left;
    color: rgba(7, 7, 15, 0.7);
  }
`

const StyledHelpModalHeader = styled(Header)`
  color: rgba(108, 24, 186, 1);
`
